import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    MdCheckCircle, MdTrendingUp, MdPeople, 
    MdVerifiedUser, MdBusiness, MdSecurity, MdStar,
    MdAndroid, MdApple, MdQrCodeScanner, MdCloudSync,
    MdNotificationsActive, MdAnalytics, MdSmartphone,
    MdDownload, MdShare, MdThumbUp
} from 'react-icons/md';
import { FaFlask, FaArrowRight, FaSeedling, FaLeaf, FaHandshake, FaGlobeAmericas, FaGooglePlay, FaApple, FaStar, FaUsers, FaChartLine, FaMobileAlt } from 'react-icons/fa';
import { GiMilkCarton, GiWheat, GiFertilizerBag } from 'react-icons/gi';
import { RiRobot2Line } from 'react-icons/ri';

const Products = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);

    useEffect(() => {
        const checkViewport = () => {
            const width = window.innerWidth;
            setIsMobile(width <= 768);
            setIsTablet(width > 768 && width <= 1024);
        };

        checkViewport();
        window.addEventListener('resize', checkViewport);

        // Scroll to top on page load
        window.scrollTo(0, 0);
        
        // Update document title for SEO
        document.title = 'Soil Testing Kit - Coming Soon | Krishidnya Agricultural Solutions';

        return () => {
            window.removeEventListener('resize', checkViewport);
        };
    }, []);

    // Soil Testing Kit Features
    const soilFeatures = [
        {
            icon: '🧪',
            title: 'Accurate Testing',
            description: 'Laboratory-grade results in just 15 minutes'
        },
        {
            icon: '📱',
            title: 'Mobile Integration',
            description: 'Connect to your smartphone for instant analysis'
        },
        {
            icon: '🌾',
            title: 'Crop Recommendations',
            description: 'AI-powered suggestions for optimal yields'
        },
        {
            icon: '📊',
            title: 'Historical Tracking',
            description: 'Monitor soil health trends over time'
        },
        {
            icon: '💧',
            title: 'Moisture Analysis',
            description: 'Precise soil moisture content measurement'
        },
        {
            icon: '🧬',
            title: 'Complete NPK Analysis',
            description: 'Comprehensive nutrient profiling'
        }
    ];

    // App Features
    const appFeatures = [
        {
            icon: <MdCloudSync />,
            title: 'Real-time Sync',
            description: 'Instant soil test results on your phone',
            color: '#2E8B57'
        },
        {
            icon: <MdAnalytics />,
            title: 'Smart Analytics',
            description: 'AI-powered crop insights & recommendations',
            color: '#FF6B35'
        },
        {
            icon: <MdNotificationsActive />,
            title: 'Smart Alerts',
            description: 'Weather, pest & crop health notifications',
            color: '#4169E1'
        },
        {
            icon: <FaChartLine />,
            title: 'Farm Dashboard',
            description: 'Track farm performance in real-time',
            color: '#8B4513'
        },
        {
            icon: <MdShare />,
            title: 'Share Reports',
            description: 'Easy sharing with agricultural experts',
            color: '#2D5A27'
        },
        {
            icon: <FaUsers />,
            title: 'Community Connect',
            description: 'Connect with other farmers in your area',
            color: '#6A5ACD'
        }
    ];

    // App Stats
    const appStats = [
        {
            number: '500+',
            label: 'Downloads',
            icon: <MdDownload />,
            color: '#2E8B57'
        },
        {
            number: '4.8★',
            label: 'User Rating',
            icon: <FaStar />,
            color: '#FFD700'
        },
        {
            number: '500+',
            label: 'Active Users',
            icon: <FaUsers />,
            color: '#4169E1'
        },
        {
            number: '5+',
            label: 'Districts',
            icon: <MdShare />,
            color: '#FF6B35'
        }
    ];

    const values = [
        {
            icon: <RiRobot2Line />,
            title: 'Technology-Driven',
            description: 'Modern tech solutions for efficient farming.'
        },
        {
            icon: <MdVerifiedUser />,
            title: 'Ethical Sourcing',
            description: 'Complete transparency in supply chain.'
        },
        {
            icon: <MdPeople />,
            title: 'Farmer-First',
            description: 'Solutions designed for farmers\' needs.'
        },
        {
            icon: <MdBusiness />,
            title: 'Expert Advisory',
            description: 'Experienced professionals guiding transformation.'
        },
        {
            icon: <FaLeaf />,
            title: 'Sustainable',
            description: 'Eco-friendly long-term practices.'
        },
        {
            icon: <MdSecurity />,
            title: 'End-to-End Support',
            description: 'Comprehensive planning to execution.'
        }
    ];

    const stats = [
        {
            number: '500+',
            label: 'Farmers Interested',
            description: 'In the first week',
            icon: <MdPeople />
        },
        {
            number: '5+',
            label: 'Districts Covered',
            description: 'Across Maharashtra',
            icon: <GiWheat />
        },
        {
            number: '98%',
            label: 'Accuracy Rate',
            description: 'In lab testing',
            icon: <MdStar />
        }
    ];

    return (
        <div className="premium-products">
            {/* SEO Meta Tags */}
            <head>
                <title>Soil Testing Kit - Coming Soon | Krishidnya Agricultural Solutions</title>
                <meta name="description" content="Discover Krishidnya's advanced Soil Testing Kit - coming soon. Get laboratory-grade soil analysis, NPK testing, pH monitoring, and AI-powered crop recommendations for better yields." />
                <meta name="keywords" content="soil testing kit, soil analysis, NPK testing, pH monitoring, crop recommendations, agricultural testing, soil health, farming equipment, Krishidnya app, farming app" />
                <meta property="og:title" content="Soil Testing Kit - Coming Soon | Krishidnya" />
                <meta property="og:description" content="Revolutionize your farming with our advanced soil testing kit - coming soon from Krishidnya" />
                <link rel="canonical" href="/products" />
            </head>

            {/* Hero Section - Soil Testing Kit Coming Soon */}
            <header className="hero-section" role="banner">
                <div className="container">
                    <div className="hero-content">
                        <div className="hero-text">
                            <div className="hero-badge">
                                <FaSeedling />
                                <span>Coming Soon - 2026</span>
                            </div>
                            
                            <h1 className="hero-title">
                                <span className="title-green">Soil Testing Kit</span>
                                <span className="title-green"> — Coming Soon</span>
                            </h1>
                            
                            <p className="hero-description">
                                Revolutionize your farming with our advanced soil testing kit. Get instant, 
                                accurate results for soil health, nutrient levels, and crop recommendations 
                                right at your fingertips. Empowering farmers with data-driven insights for 
                                better yields and sustainable farming.
                            </p>
                            
                            <div className="hero-actions">
                                <Link to="/contact" className="btn-primary" aria-label="Join the waitlist for Soil Testing Kit">
                                    <span>Join Waitlist</span>
                                    <FaArrowRight className="btn-icon" />
                                </Link>
                                <Link to="/services" className="btn-secondary" aria-label="Explore our agricultural services">
                                    Learn More
                                </Link>
                            </div>
                        </div>
                        
                        <div className="hero-stats">
                            <div className="stats-grid">
                                {stats.map((stat, index) => (
                                    <div key={index} className="stat-preview">
                                        <div className="stat-preview-icon">
                                            {stat.icon}
                                        </div>
                                        <div className="stat-preview-content">
                                            <div className="stat-preview-number">{stat.number}</div>
                                            <div className="stat-preview-label">{stat.label}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="hero-bottom">
                    <div className="container">
                        <div className="scroll-indicator">
                            <span>Scroll to explore</span>
                            <div className="scroll-line"></div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Features Section */}
            <section className="features-section" aria-labelledby="features-heading">
                <div className="container">
                    <div className="section-intro">
                        <h2 id="features-heading">Why Choose Our <span className="title-green">Soil Testing Kit</span></h2>
                        <p className="section-description">
                            Advanced features designed to give you comprehensive soil health insights
                        </p>
                    </div>
                    
                    <div className="features-grid">
                        {soilFeatures.map((feature, index) => (
                            <article key={index} className="feature-card">
                                <div className="feature-icon" aria-hidden="true">
                                    {feature.icon}
                                </div>
                                <div className="feature-content">
                                    <h3>{feature.title}</h3>
                                    <p>{feature.description}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Krishidnya App Section - Enhanced Premium Design */}
            <section className="app-section" aria-labelledby="app-heading">
                <div className="container">
                    <div className="app-wrapper">
                        <div className="app-header">
                            <div className="app-badge">
                                <span className="app-badge-icon">📱</span>
                                Available on Play Store
                            </div>
                            <h2 id="app-heading">
                                Download the <span className="title-green">Krishidnya App</span>
                            </h2>
                            <p className="app-subtitle">
                                Manage your farm, get real-time insights, and access premium agricultural 
                                services right from your smartphone
                            </p>
                        </div>

                        <div className="app-content-wrapper">
                            {/* Left Column - App Info */}
                            <div className="app-info-column">
                                {/* App Stats */}
                                <div className="app-stats-grid">
                                    {appStats.map((stat, index) => (
                                        <div key={index} className="app-stat-card">
                                            <div className="app-stat-icon" style={{ color: stat.color }}>
                                                {stat.icon}
                                            </div>
                                            <div className="app-stat-number">{stat.number}</div>
                                            <div className="app-stat-label">{stat.label}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* App Features */}
                                <div className="app-features-grid">
                                    {appFeatures.map((feature, index) => (
                                        <div key={index} className="app-feature-item">
                                            <div className="app-feature-icon-wrapper" style={{ background: `${feature.color}15` }}>
                                                <span style={{ color: feature.color }}>{feature.icon}</span>
                                            </div>
                                            <div className="app-feature-content">
                                                <h4>{feature.title}</h4>
                                                <p>{feature.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Download Section */}
                                <div className="app-download-section">
                                    <div className="app-download-header">
                                        <span className="download-title">Get the App Now</span>
                                        <span className="download-subtitle">Available on Google Play Store</span>
                                    </div>
                                    <div className="app-download-buttons">
                                        <a 
                                            href="https://play.google.com/store/apps/details?id=com.krishidnya.app" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="download-btn play-store"
                                            aria-label="Download Krishidnya app from Google Play Store"
                                        >
                                            <FaGooglePlay className="store-icon" />
                                            <div className="store-text">
                                                <span className="store-small">GET IT ON</span>
                                                <span className="store-large">Google Play</span>
                                            </div>
                                            <span className="download-badge">Free</span>
                                        </a>
                                        <a 
                                            href="#" 
                                            className="download-btn coming-soon-btn"
                                            aria-label="App Store coming soon"
                                        >
                                            <FaApple className="store-icon" />
                                            <div className="store-text">
                                                <span className="store-small">COMING SOON</span>
                                                <span className="store-large">App Store</span>
                                            </div>
                                            <span className="download-badge coming">Soon</span>
                                        </a>
                                    </div>
                                    <div className="app-qr-container">
                                        <div className="qr-code-wrapper">
                                            <div className="qr-box">
                                                <MdQrCodeScanner className="qr-icon" />
                                                <div className="qr-pulse"></div>
                                            </div>
                                            <div className="qr-info">
                                                <span className="qr-title">Scan to Download</span>
                                                <span className="qr-subtitle">Available on Play Store</span>
                                            </div>
                                        </div>
                                        <div className="app-trust-badges">
                                            <div className="trust-badge">
                                                <MdCheckCircle className="trust-icon" />
                                                <span>100% Secure</span>
                                            </div>
                                            <div className="trust-badge">
                                                <MdCheckCircle className="trust-icon" />
                                                <span>Free Download</span>
                                            </div>
                                            <div className="trust-badge">
                                                <MdCheckCircle className="trust-icon" />
                                                <span>Regular Updates</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Phone Mockup */}
                            <div className="app-visual-column">
                                <div className="phone-container">
                                    <div className="phone-mockup">
                                        <div className="phone-notch"></div>
                                        <div className="phone-screen">
                                            <div className="phone-status-bar">
                                                <span className="phone-time">10:30</span>
                                                <div className="phone-status-icons">
                                                    <span>📶</span>
                                                    <span>📶</span>
                                                    <span>🔋</span>
                                                </div>
                                            </div>
                                            <div className="phone-app-content">
                                                <div className="phone-app-header">
                                                    <div className="phone-app-logo">
                                                        <FaSeedling />
                                                        <span>Krishidnya</span>
                                                    </div>
                                                    <span className="phone-app-badge">v2.0</span>
                                                </div>
                                                
                                                <div className="phone-dashboard">
                                                    <div className="phone-dashboard-header">
                                                        <span className="dashboard-title">Soil Health Dashboard</span>
                                                        <span className="dashboard-date">Today</span>
                                                    </div>
                                                    
                                                    <div className="phone-metrics">
                                                        <div className="phone-metric">
                                                            <span className="metric-label">pH Level</span>
                                                            <span className="metric-value green">6.8</span>
                                                            <span className="metric-status">Optimal</span>
                                                        </div>
                                                        <div className="phone-metric">
                                                            <span className="metric-label">NPK</span>
                                                            <span className="metric-value green">Good</span>
                                                            <span className="metric-status">Balanced</span>
                                                        </div>
                                                        <div className="phone-metric">
                                                            <span className="metric-label">Moisture</span>
                                                            <span className="metric-value blue">42%</span>
                                                            <span className="metric-status">Ideal</span>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="phone-recommendations">
                                                        <span className="rec-title">🌾 Crop Recommendations</span>
                                                        <div className="rec-item">
                                                            <span>Add organic fertilizer</span>
                                                            <span className="rec-tag">High Priority</span>
                                                        </div>
                                                        <div className="rec-item">
                                                            <span>Increase irrigation</span>
                                                            <span className="rec-tag medium">Medium</span>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="phone-bottom-nav">
                                                        <span className="nav-active">🏠</span>
                                                        <span>📊</span>
                                                        <span>🌾</span>
                                                        <span>👤</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="phone-home-button"></div>
                                    </div>
                                    
                                    {/* Floating Elements */}
                                    <div className="phone-float-element float-1">🧪</div>
                                    <div className="phone-float-element float-2">📊</div>
                                    <div className="phone-float-element float-3">🌱</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Waitlist Section */}
            <section className="waitlist-section" aria-labelledby="waitlist-heading">
                <div className="container">
                    <div className="waitlist-container">
                        <div className="waitlist-content">
                            <h2 id="waitlist-heading">Join the <span className="title-green">Waitlist</span></h2>
                            <p className="waitlist-description">
                                Be among the first farmers to get our advanced soil testing kit. 
                                Join our exclusive waitlist and get early access, special launch pricing, 
                                and a free 30-minute consultation with our agricultural experts.
                            </p>
                            
                            <div className="waitlist-benefits">
                                <div className="benefit-item">
                                    <MdCheckCircle className="benefit-check" />
                                    <span>Early access to the first batch</span>
                                </div>
                                <div className="benefit-item">
                                    <MdCheckCircle className="benefit-check" />
                                    <span>Special launch discount</span>
                                </div>
                                <div className="benefit-item">
                                    <MdCheckCircle className="benefit-check" />
                                    <span>Free expert consultation</span>
                                </div>
                                <div className="benefit-item">
                                    <MdCheckCircle className="benefit-check" />
                                    <span>Lifetime software updates</span>
                                </div>
                            </div>
                            
                            <div className="waitlist-form-container">
                                <form className="waitlist-form" onSubmit={(e) => e.preventDefault()}>
                                    <input 
                                        type="email" 
                                        placeholder="Enter your email address"
                                        className="waitlist-input"
                                        aria-label="Email for waitlist"
                                        required
                                    />
                                    <button type="submit" className="btn-primary waitlist-submit">
                                        Join Waitlist
                                        <FaArrowRight className="btn-icon" />
                                    </button>
                                </form>
                                <p className="waitlist-note">🔒 No spam. Unsubscribe anytime. We respect your privacy.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Impact Section - Same as Home */}
            <section className="impact-section" aria-labelledby="impact-heading">
                <div className="container">
                    <div className="section-intro">
                        <h2 id="impact-heading">Transforming Agriculture Across India</h2>
                        <p className="section-description">
                            Driving meaningful change and measurable impact across the agricultural landscape
                        </p>
                    </div>
                    
                    <div className="impact-grid" itemScope itemType="https://schema.org/Organization">
                        {[
                            {
                                number: '500+',
                                label: 'Farmers Empowered',
                                description: 'Across India',
                                icon: <MdPeople />
                            },
                            {
                                number: '45%+',
                                label: 'Yield Improvement',
                                description: 'Average increase',
                                icon: <MdTrendingUp />
                            },
                            {
                                number: '5+',
                                label: 'Districts Covered',
                                description: 'With our solutions',
                                icon: <GiWheat />
                            },
                            {
                                number: '98%',
                                label: 'Success Rate',
                                description: 'Farmer satisfaction',
                                icon: <MdStar />
                            }
                        ].map((stat, index) => (
                            <div key={index} className="impact-card">
                                <div className="impact-icon" aria-hidden="true">
                                    {stat.icon}
                                </div>
                                <div className="impact-number">{stat.number}</div>
                                <div className="impact-label">{stat.label}</div>
                                <p className="impact-desc">{stat.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values Section - Same as Home */}
            <section className="values-section" aria-labelledby="values-heading">
                <div className="container">
                    <div className="section-intro">
                        <h2 id="values-heading">Why Choose Krishidnya</h2>
                        <p className="section-description">
                            The principles that guide our commitment to agricultural excellence and farmer prosperity
                        </p>
                    </div>
                    
                    <div className="values-grid">
                        {values.map((value, index) => (
                            <article key={index} className="value-card">
                                <div className="value-icon" aria-hidden="true">
                                    {value.icon}
                                </div>
                                <h3>{value.title}</h3>
                                <p>{value.description}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA - Same as Home */}
            <section className="final-cta" aria-labelledby="cta-heading">
                <div className="container">
                    <div className="cta-content">
                        <h2 id="cta-heading">Ready to Transform Your Farming?</h2>
                        <p>Connect with us to discover how our technology-driven solutions can transform your farming operations and increase profitability.</p>
                        
                        <div className="cta-actions">
                            <Link to="/contact" className="btn-primary" aria-label="Get started with Krishidnya solutions">
                                Get Started
                            </Link>
                            <Link to="/services" className="btn-secondary" aria-label="Request a demo of our agricultural solutions">
                                Explore Services
                            </Link>
                        </div>
                        
                        <div className="cta-features">
                            <div className="feature">
                                <MdCheckCircle /> <span>Free Consultation</span>
                            </div>
                            <div className="feature">
                                <MdCheckCircle /> <span>Custom Solutions</span>
                            </div>
                            <div className="feature">
                                <MdCheckCircle /> <span>24/7 Support</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <style jsx>{`
                @import url('https://fonts.googleapis.com/css2?family=Source+Serif+Pro:wght@400;600;700&family=Inter:wght@300;400;500;600;700&display=swap');

                :root {
                    --primary-black: #000000;
                    --secondary-black: #1A1A1A;
                    --medium-black: #333333;
                    --light-black: #666666;
                    --primary-green: #2E8B57;
                    --secondary-green: #3A7D34;
                    --light-green: #4CAF7A;
                    --accent-green: #2D5A27;
                    --white: #FFFFFF;
                    --off-white: #FAFAFA;
                    --light-gray: #F5F5F5;
                    --medium-gray: #E8E8E8;
                    --border-color: #E0E0E0;
                    --text-primary: #000000;
                    --text-secondary: #333333;
                    --text-light: #666666;
                    --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
                    --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
                    --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
                    --radius-sm: 6px;
                    --radius-md: 10px;
                    --radius-lg: 16px;
                    --radius-xl: 20px;
                    --transition: all 0.3s ease;
                    --header-height: ${isMobile ? '70px' : '80px'};
                }

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                .premium-products {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    color: var(--text-primary);
                    background: var(--white);
                    line-height: 1.6;
                    overflow-x: hidden;
                    scroll-behavior: smooth;
                }

                .container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 ${isMobile ? '20px' : '24px'};
                }

                /* Typography - Same as Home */
                h1, h2, h3, h4 {
                    font-family: 'Source Serif Pro', serif;
                    font-weight: 600;
                    line-height: 1.2;
                    letter-spacing: -0.01em;
                    color: var(--primary-black);
                }

                h1 { 
                    font-size: ${isMobile ? '2.25rem' : isTablet ? '3rem' : '3.5rem'};
                    font-weight: 700;
                    letter-spacing: -0.02em;
                }

                h2 { 
                    font-size: ${isMobile ? '1.75rem' : isTablet ? '2.25rem' : '2.5rem'};
                    margin-bottom: 1rem;
                }

                h3 { 
                    font-size: ${isMobile ? '1.25rem' : isTablet ? '1.5rem' : '1.75rem'};
                    margin-bottom: 0.75rem;
                }

                p { 
                    font-size: ${isMobile ? '1rem' : '1.125rem'};
                    color: var(--text-secondary);
                    line-height: 1.7;
                    font-weight: 400;
                }

                .section-description {
                    font-size: ${isMobile ? '1rem' : '1.125rem'};
                    color: var(--text-light);
                    margin-top: 0.5rem;
                    font-weight: 400;
                }

                .title-green {
                    color: var(--primary-green);
                }

                /* Buttons - Same as Home */
                .btn-primary, .btn-secondary {
                    padding: ${isMobile ? '12px 24px' : '14px 32px'};
                    font-size: ${isMobile ? '0.95rem' : '1rem'};
                    font-family: 'Inter', sans-serif;
                    font-weight: 600;
                    border-radius: var(--radius-md);
                    text-decoration: none;
                    transition: var(--transition);
                    border: 2px solid transparent;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    text-align: center;
                    min-width: ${isMobile ? '140px' : 'auto'};
                }

                .btn-primary {
                    background: var(--primary-black);
                    color: var(--white);
                }

                .btn-primary:hover, .btn-primary:focus {
                    background: var(--secondary-black);
                    transform: translateY(-2px);
                    box-shadow: var(--shadow-md);
                    outline: 2px solid var(--primary-green);
                }

                .btn-secondary {
                    background: transparent;
                    color: var(--primary-black);
                    border: 2px solid var(--primary-black);
                }

                .btn-secondary:hover, .btn-secondary:focus {
                    background: var(--primary-black);
                    color: var(--white);
                    transform: translateY(-2px);
                    outline: 2px solid var(--primary-green);
                }

                .btn-icon {
                    font-size: ${isMobile ? '1rem' : '1.125rem'};
                    transition: transform 0.3s ease;
                }

                .btn-primary:hover .btn-icon, .btn-primary:focus .btn-icon {
                    transform: translateX(4px);
                }

                /* Hero Section - Same as Home */
                .hero-section {
                    padding: ${isMobile ? 'calc(var(--header-height) + 40px) 0 40px' : 'calc(var(--header-height) + 60px) 0 60px'};
                    background: var(--white);
                    position: relative;
                    margin-top: 0;
                }

                .hero-section::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 1px;
                    background: linear-gradient(90deg, transparent, var(--border-color), transparent);
                }

                .hero-content {
                    display: flex;
                    flex-direction: ${isMobile ? 'column' : 'row'};
                    gap: ${isMobile ? '40px' : '60px'};
                    align-items: center;
                    position: relative;
                    z-index: 1;
                }

                .hero-text {
                    flex: 1;
                }

                .hero-stats {
                    flex: ${isMobile ? '0 0 auto' : '1'};
                    width: ${isMobile ? '100%' : 'auto'};
                }

                .hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    background: rgba(46, 139, 87, 0.1);
                    color: var(--primary-green);
                    padding: ${isMobile ? '8px 16px' : '10px 20px'};
                    border-radius: 50px;
                    font-size: ${isMobile ? '0.85rem' : '14px'};
                    font-weight: 600;
                    margin-bottom: ${isMobile ? '20px' : '30px'};
                    border: 1px solid rgba(46, 139, 87, 0.2);
                }

                .hero-badge svg {
                    font-size: ${isMobile ? '0.9rem' : '16px'};
                }

                .hero-title {
                    margin-bottom: ${isMobile ? '1rem' : '1.5rem'};
                    line-height: 1.1;
                }

                .hero-description {
                    margin-bottom: ${isMobile ? '2rem' : '3rem'};
                    font-size: ${isMobile ? '1.063rem' : '1.125rem'};
                    line-height: 1.6;
                }

                .hero-actions {
                    display: flex;
                    gap: ${isMobile ? '0.75rem' : '1rem'};
                    flex-direction: ${isMobile ? 'column' : 'row'};
                    align-items: ${isMobile ? 'stretch' : 'flex-start'};
                }

                /* Stats Preview - Same as Home */
                .stats-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: ${isMobile ? '1rem' : '1.5rem'};
                    background: var(--off-white);
                    padding: ${isMobile ? '1.5rem' : '2rem'};
                    border-radius: var(--radius-lg);
                    border: 1px solid var(--border-color);
                }

                .stat-preview {
                    display: flex;
                    align-items: center;
                    gap: ${isMobile ? '0.75rem' : '1rem'};
                }

                .stat-preview-icon {
                    width: ${isMobile ? '40px' : '48px'};
                    height: ${isMobile ? '40px' : '48px'};
                    background: var(--white);
                    border-radius: var(--radius-md);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: ${isMobile ? '1.25rem' : '1.5rem'};
                    color: var(--primary-green);
                    flex-shrink: 0;
                    border: 1px solid var(--border-color);
                }

                .stat-preview-content {
                    flex: 1;
                }

                .stat-preview-number {
                    font-size: ${isMobile ? '1.5rem' : '1.75rem'};
                    font-weight: 700;
                    color: var(--primary-black);
                    margin-bottom: 0.25rem;
                    font-family: 'Source Serif Pro', serif;
                }

                .stat-preview-label {
                    font-size: ${isMobile ? '0.75rem' : '0.875rem'};
                    color: var(--text-light);
                    line-height: 1.4;
                    font-weight: 500;
                }

                /* Scroll Indicator - Same as Home */
                .hero-bottom {
                    padding-top: ${isMobile ? '2rem' : '3rem'};
                }

                .scroll-indicator {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                    color: var(--text-light);
                    font-size: 0.875rem;
                }

                .scroll-line {
                    width: 1px;
                    height: 40px;
                    background: var(--border-color);
                    position: relative;
                }

                .scroll-line::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 20px;
                    background: var(--primary-green);
                    animation: scroll 2s infinite;
                }

                /* Section Common - Same as Home */
                section {
                    padding: ${isMobile ? '80px 0' : '100px 0'};
                    scroll-margin-top: var(--header-height);
                }

                .section-intro {
                    text-align: center;
                    max-width: 700px;
                    margin: 0 auto ${isMobile ? '2rem' : '3rem'};
                }

                /* Features Section */
                .features-section {
                    background: var(--light-gray);
                }

                .features-grid {
                    display: grid;
                    grid-template-columns: ${isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'};
                    gap: ${isMobile ? '1.5rem' : '2rem'};
                }

                .feature-card {
                    display: flex;
                    gap: 1rem;
                    padding: ${isMobile ? '1.25rem' : '1.5rem'};
                    background: var(--white);
                    border: 1px solid var(--border-color);
                    border-radius: var(--radius-lg);
                    transition: var(--transition);
                }

                .feature-card:hover {
                    transform: translateY(-4px);
                    box-shadow: var(--shadow-md);
                    border-color: var(--primary-green);
                }

                .feature-icon {
                    font-size: ${isMobile ? '28px' : '32px'};
                    flex-shrink: 0;
                }

                .feature-content h3 {
                    font-size: ${isMobile ? '1.1rem' : '1.25rem'};
                    margin-bottom: 0.25rem;
                }

                .feature-content p {
                    font-size: ${isMobile ? '0.9rem' : '0.95rem'};
                    color: var(--text-light);
                    margin: 0;
                }

                /* ===== APP SECTION - ENHANCED PREMIUM DESIGN ===== */
                .app-section {
                    background: var(--white);
                    padding: ${isMobile ? '60px 0' : '80px 0'};
                }

                .app-wrapper {
                    background: linear-gradient(135deg, #f8fbf9 0%, #e8f5e9 100%);
                    border-radius: var(--radius-xl);
                    padding: ${isMobile ? '2rem 1.5rem' : '3rem 3rem'};
                    border: 1px solid var(--border-color);
                    position: relative;
                    overflow: hidden;
                }

                .app-wrapper::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    right: -20%;
                    width: 400px;
                    height: 400px;
                    background: radial-gradient(circle, rgba(46, 139, 87, 0.05) 0%, transparent 70%);
                    border-radius: 50%;
                    pointer-events: none;
                }

                .app-header {
                    text-align: center;
                    margin-bottom: ${isMobile ? '2rem' : '3rem'};
                    position: relative;
                    z-index: 1;
                }

                .app-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(46, 139, 87, 0.12);
                    color: var(--primary-green);
                    padding: 8px 16px;
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    margin-bottom: 1rem;
                    border: 1px solid rgba(46, 139, 87, 0.2);
                }

                .app-badge-icon {
                    font-size: 18px;
                }

                .app-subtitle {
                    font-size: ${isMobile ? '1rem' : '1.125rem'};
                    color: var(--text-light);
                    max-width: 600px;
                    margin: 0 auto;
                }

                .app-content-wrapper {
                    display: grid;
                    grid-template-columns: ${isMobile ? '1fr' : '1fr 1fr'};
                    gap: ${isMobile ? '2rem' : '3rem'};
                    align-items: start;
                    position: relative;
                    z-index: 1;
                }

                /* App Info Column */
                .app-info-column {
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                /* App Stats */
                .app-stats-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 0.75rem;
                }

                .app-stat-card {
                    background: var(--white);
                    padding: ${isMobile ? '0.75rem' : '1rem'};
                    border-radius: var(--radius-md);
                    text-align: center;
                    border: 1px solid var(--border-color);
                    transition: var(--transition);
                }

                .app-stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: var(--shadow-sm);
                    border-color: var(--primary-green);
                }

                .app-stat-icon {
                    font-size: ${isMobile ? '1.25rem' : '1.5rem'};
                    margin-bottom: 4px;
                }

                .app-stat-number {
                    font-size: ${isMobile ? '1.1rem' : '1.25rem'};
                    font-weight: 700;
                    font-family: 'Source Serif Pro', serif;
                    color: var(--primary-black);
                }

                .app-stat-label {
                    font-size: ${isMobile ? '0.6rem' : '0.7rem'};
                    color: var(--text-light);
                    font-weight: 500;
                }

                /* App Features Grid */
                .app-features-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 0.75rem;
                }

                .app-feature-item {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.75rem;
                    background: var(--white);
                    border-radius: var(--radius-md);
                    border: 1px solid var(--border-color);
                    transition: var(--transition);
                }

                .app-feature-item:hover {
                    transform: translateX(4px);
                    border-color: var(--primary-green);
                    box-shadow: var(--shadow-sm);
                }

                .app-feature-icon-wrapper {
                    width: 36px;
                    height: 36px;
                    border-radius: var(--radius-sm);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    flex-shrink: 0;
                }

                .app-feature-content h4 {
                    font-size: 0.85rem;
                    font-weight: 600;
                    font-family: 'Inter', sans-serif;
                    margin-bottom: 1px;
                }

                .app-feature-content p {
                    font-size: 0.75rem;
                    color: var(--text-light);
                    margin: 0;
                }

                /* Download Section */
                .app-download-section {
                    background: var(--white);
                    border-radius: var(--radius-lg);
                    padding: ${isMobile ? '1.25rem' : '1.5rem'};
                    border: 1px solid var(--border-color);
                }

                .app-download-header {
                    margin-bottom: 1rem;
                }

                .download-title {
                    font-size: 1rem;
                    font-weight: 700;
                    display: block;
                    color: var(--primary-black);
                }

                .download-subtitle {
                    font-size: 0.85rem;
                    color: var(--text-light);
                }

                .app-download-buttons {
                    display: flex;
                    gap: 0.75rem;
                    flex-direction: ${isMobile ? 'column' : 'row'};
                    margin-bottom: 1rem;
                }

                .download-btn {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 16px;
                    border-radius: var(--radius-md);
                    text-decoration: none;
                    transition: var(--transition);
                    border: 2px solid var(--border-color);
                    background: var(--white);
                    flex: 1;
                    position: relative;
                }

                .download-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: var(--shadow-md);
                }

                .play-store {
                    border-color: var(--primary-green);
                    background: var(--white);
                }

                .play-store:hover {
                    border-color: var(--primary-green);
                    box-shadow: 0 4px 16px rgba(46, 139, 87, 0.2);
                }

                .coming-soon-btn {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .store-icon {
                    font-size: 28px;
                    color: var(--text-secondary);
                }

                .play-store .store-icon {
                    color: var(--primary-green);
                }

                .store-text {
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                }

                .store-small {
                    font-size: 9px;
                    color: var(--text-light);
                    letter-spacing: 0.5px;
                }

                .store-large {
                    font-size: 16px;
                    font-weight: 700;
                    color: var(--text-primary);
                }

                .download-badge {
                    font-size: 10px;
                    font-weight: 700;
                    padding: 2px 8px;
                    border-radius: 50px;
                    background: var(--primary-green);
                    color: var(--white);
                    letter-spacing: 0.5px;
                }

                .download-badge.coming {
                    background: var(--light-gray);
                    color: var(--text-light);
                }

                .app-qr-container {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 1rem;
                    padding-top: 1rem;
                    border-top: 1px solid var(--border-color);
                }

                .qr-code-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .qr-box {
                    width: 60px;
                    height: 60px;
                    background: var(--off-white);
                    border: 2px dashed var(--border-color);
                    border-radius: var(--radius-md);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                }

                .qr-icon {
                    font-size: 30px;
                    color: var(--primary-green);
                }

                .qr-pulse {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    border: 2px solid var(--primary-green);
                    border-radius: var(--radius-md);
                    animation: qrPulse 2s ease-in-out infinite;
                }

                @keyframes qrPulse {
                    0%, 100% { transform: scale(1); opacity: 0.5; }
                    50% { transform: scale(1.1); opacity: 0; }
                }

                .qr-info {
                    display: flex;
                    flex-direction: column;
                }

                .qr-title {
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: var(--primary-black);
                }

                .qr-subtitle {
                    font-size: 0.8rem;
                    color: var(--text-light);
                }

                .app-trust-badges {
                    display: flex;
                    gap: 1rem;
                }

                .trust-badge {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 0.75rem;
                    color: var(--text-secondary);
                }

                .trust-icon {
                    color: var(--primary-green);
                    font-size: 14px;
                }

                /* ===== PHONE MOCKUP - ENHANCED ===== */
                .app-visual-column {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    position: relative;
                }

                .phone-container {
                    position: relative;
                    width: ${isMobile ? '260px' : '320px'};
                    height: ${isMobile ? '480px' : '580px'};
                }

                .phone-mockup {
                    width: 100%;
                    height: 100%;
                    background: var(--secondary-black);
                    border-radius: 40px;
                    padding: 12px;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                    position: relative;
                }

                .phone-notch {
                    position: absolute;
                    top: 12px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 120px;
                    height: 24px;
                    background: var(--secondary-black);
                    border-radius: 0 0 12px 12px;
                    z-index: 10;
                }

                .phone-notch::before {
                    content: '';
                    position: absolute;
                    top: 4px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 60px;
                    height: 6px;
                    background: var(--medium-black);
                    border-radius: 3px;
                }

                .phone-screen {
                    width: 100%;
                    height: 100%;
                    background: var(--white);
                    border-radius: 30px;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .phone-status-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px 16px 4px;
                    font-size: 11px;
                    font-weight: 600;
                    color: var(--text-secondary);
                    background: var(--white);
                }

                .phone-status-icons {
                    display: flex;
                    gap: 4px;
                }

                .phone-app-content {
                    flex: 1;
                    padding: 8px 12px 12px;
                    background: var(--light-gray);
                    overflow: hidden;
                }

                .phone-app-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }

                .phone-app-logo {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-weight: 700;
                    font-size: 16px;
                    color: var(--primary-green);
                }

                .phone-app-logo svg {
                    font-size: 18px;
                }

                .phone-app-badge {
                    font-size: 9px;
                    padding: 2px 8px;
                    background: var(--primary-green);
                    color: var(--white);
                    border-radius: 50px;
                    font-weight: 600;
                }

                .phone-dashboard {
                    background: var(--white);
                    border-radius: var(--radius-md);
                    padding: 10px 12px;
                    border: 1px solid var(--border-color);
                }

                .phone-dashboard-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                }

                .dashboard-title {
                    font-size: 11px;
                    font-weight: 700;
                    color: var(--text-secondary);
                }

                .dashboard-date {
                    font-size: 9px;
                    color: var(--text-light);
                }

                .phone-metrics {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 6px;
                    margin-bottom: 8px;
                }

                .phone-metric {
                    background: var(--off-white);
                    padding: 6px 8px;
                    border-radius: var(--radius-sm);
                    text-align: center;
                }

                .metric-label {
                    font-size: 8px;
                    color: var(--text-light);
                    display: block;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .metric-value {
                    font-size: 16px;
                    font-weight: 700;
                    font-family: 'Source Serif Pro', serif;
                    display: block;
                }

                .metric-value.green {
                    color: var(--primary-green);
                }

                .metric-value.blue {
                    color: #4169E1;
                }

                .metric-status {
                    font-size: 7px;
                    color: var(--text-light);
                    display: block;
                }

                .phone-recommendations {
                    margin-top: 8px;
                    padding-top: 8px;
                    border-top: 1px solid var(--border-color);
                }

                .rec-title {
                    font-size: 10px;
                    font-weight: 600;
                    color: var(--text-secondary);
                    display: block;
                    margin-bottom: 4px;
                }

                .rec-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 4px 0;
                    font-size: 10px;
                    color: var(--text-secondary);
                }

                .rec-tag {
                    font-size: 8px;
                    padding: 1px 6px;
                    border-radius: 50px;
                    background: #FFE5E5;
                    color: #FF4444;
                    font-weight: 600;
                }

                .rec-tag.medium {
                    background: #FFF3E0;
                    color: #FF9800;
                }

                .phone-bottom-nav {
                    display: flex;
                    justify-content: space-around;
                    padding: 8px 0 4px;
                    border-top: 1px solid var(--border-color);
                    margin-top: 8px;
                    font-size: 16px;
                    color: var(--text-light);
                }

                .phone-bottom-nav .nav-active {
                    color: var(--primary-green);
                }

                .phone-home-button {
                    position: absolute;
                    bottom: 8px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 40px;
                    height: 4px;
                    background: rgba(255, 255, 255, 0.3);
                    border-radius: 2px;
                }

                /* Floating Elements */
                .phone-float-element {
                    position: absolute;
                    font-size: 24px;
                    animation: floatElement 4s ease-in-out infinite;
                }

                .float-1 {
                    top: -20px;
                    right: -20px;
                    animation-delay: 0s;
                }

                .float-2 {
                    bottom: 40px;
                    left: -25px;
                    animation-delay: 1s;
                }

                .float-3 {
                    bottom: -15px;
                    right: 15px;
                    animation-delay: 2s;
                }

                @keyframes floatElement {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    25% { transform: translateY(-10px) rotate(5deg); }
                    75% { transform: translateY(10px) rotate(-5deg); }
                }

                /* Waitlist Section */
                .waitlist-section {
                    background: var(--light-gray);
                }

                .waitlist-container {
                    max-width: 700px;
                    margin: 0 auto;
                }

                .waitlist-content {
                    text-align: center;
                }

                .waitlist-description {
                    margin-bottom: 1.5rem;
                }

                .waitlist-benefits {
                    display: grid;
                    grid-template-columns: ${isMobile ? '1fr' : '1fr 1fr'};
                    gap: 0.75rem;
                    margin-bottom: 2rem;
                    text-align: left;
                }

                .benefit-item {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: ${isMobile ? '0.85rem' : '0.9rem'};
                    color: var(--text-secondary);
                }

                .benefit-check {
                    color: var(--primary-green);
                    flex-shrink: 0;
                }

                .waitlist-form-container {
                    margin-top: 0.5rem;
                }

                .waitlist-form {
                    display: flex;
                    gap: 12px;
                    flex-direction: ${isMobile ? 'column' : 'row'};
                }

                .waitlist-input {
                    flex: 1;
                    padding: 12px 16px;
                    border: 2px solid var(--border-color);
                    border-radius: var(--radius-md);
                    font-family: 'Inter', sans-serif;
                    font-size: 15px;
                    transition: var(--transition);
                }

                .waitlist-input:focus {
                    outline: none;
                    border-color: var(--primary-green);
                    box-shadow: 0 0 0 4px rgba(46, 139, 87, 0.1);
                }

                .waitlist-submit {
                    white-space: nowrap;
                }

                .waitlist-note {
                    font-size: 13px;
                    color: var(--text-light);
                    margin-top: 12px;
                }

                /* Impact Section - Same as Home */
                .impact-section {
                    background: var(--white);
                }

                .impact-grid {
                    display: grid;
                    grid-template-columns: ${isMobile ? '1fr 1fr' : 'repeat(4, 1fr)'};
                    gap: ${isMobile ? '1rem' : '1.5rem'};
                }

                .impact-card {
                    background: var(--off-white);
                    border: 1px solid var(--border-color);
                    border-radius: var(--radius-lg);
                    padding: ${isMobile ? '1.5rem 1rem' : '2rem 1.5rem'};
                    text-align: center;
                    transition: var(--transition);
                }

                .impact-card:hover {
                    transform: translateY(-4px);
                    box-shadow: var(--shadow-md);
                    border-color: var(--primary-green);
                }

                .impact-icon {
                    font-size: ${isMobile ? '2rem' : '2.5rem'};
                    color: var(--primary-green);
                    margin-bottom: ${isMobile ? '0.75rem' : '1rem'};
                }

                .impact-number {
                    font-size: ${isMobile ? '1.75rem' : '2rem'};
                    font-weight: 700;
                    color: var(--primary-black);
                    margin-bottom: 0.25rem;
                    font-family: 'Source Serif Pro', serif;
                }

                .impact-label {
                    font-size: ${isMobile ? '0.875rem' : '1rem'};
                    font-weight: 600;
                    color: var(--text-secondary);
                    margin-bottom: 0.25rem;
                }

                .impact-desc {
                    font-size: ${isMobile ? '0.75rem' : '0.875rem'};
                    color: var(--text-light);
                    margin: 0;
                }

                /* Values Section - Same as Home */
                .values-section {
                    background: var(--light-gray);
                }

                .values-grid {
                    display: grid;
                    grid-template-columns: ${isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'};
                    gap: ${isMobile ? '1.5rem' : '2rem'};
                }

                .value-card {
                    background: var(--white);
                    border: 1px solid var(--border-color);
                    border-radius: var(--radius-lg);
                    padding: ${isMobile ? '1.5rem 1.25rem' : '2rem 1.75rem'};
                    text-align: center;
                    transition: var(--transition);
                }

                .value-card:hover {
                    transform: translateY(-4px);
                    box-shadow: var(--shadow-md);
                    border-color: var(--primary-green);
                }

                .value-icon {
                    font-size: ${isMobile ? '2rem' : '2.5rem'};
                    color: var(--primary-green);
                    margin-bottom: ${isMobile ? '1rem' : '1.25rem'};
                }

                .value-card h3 {
                    margin-bottom: ${isMobile ? '0.5rem' : '0.75rem'};
                }

                .value-card p {
                    font-size: ${isMobile ? '0.95rem' : '1rem'};
                    color: var(--text-light);
                    line-height: 1.6;
                    margin: 0;
                }

                /* Final CTA - Same as Home */
                .final-cta {
                    background: var(--white);
                }

                .cta-content {
                    text-align: center;
                    max-width: 700px;
                    margin: 0 auto;
                }

                .cta-content h2 {
                    margin-bottom: ${isMobile ? '0.75rem' : '1rem'};
                }

                .cta-content p {
                    font-size: ${isMobile ? '1.063rem' : '1.125rem'};
                    margin-bottom: ${isMobile ? '2rem' : '2.5rem'};
                    color: var(--text-secondary);
                }

                .cta-actions {
                    display: flex;
                    gap: ${isMobile ? '0.75rem' : '1rem'};
                    justify-content: center;
                    margin-bottom: ${isMobile ? '2rem' : '2.5rem'};
                    flex-direction: ${isMobile ? 'column' : 'row'};
                    align-items: ${isMobile ? 'stretch' : 'center'};
                }

                .final-cta .btn-primary,
                .final-cta .btn-secondary {
                    min-width: ${isMobile ? '100%' : '200px'};
                }

                .cta-features {
                    display: grid;
                    grid-template-columns: ${isMobile ? '1fr' : 'repeat(3, 1fr)'};
                    gap: ${isMobile ? '1rem' : '1.5rem'};
                    justify-content: center;
                }

                .cta-features .feature {
                    display: flex;
                    align-items: center;
                    justify-content: ${isMobile ? 'flex-start' : 'center'};
                    gap: 0.5rem;
                    color: var(--primary-green);
                    font-weight: 500;
                    font-size: ${isMobile ? '0.875rem' : '1rem'};
                }

                .cta-features .feature svg {
                    color: var(--primary-green);
                    flex-shrink: 0;
                }

                /* Animations */
                @keyframes scroll {
                    0% {
                        transform: translateY(0);
                        opacity: 1;
                    }
                    100% {
                        transform: translateY(20px);
                        opacity: 0;
                    }
                }

                /* Focus styles for accessibility */
                a:focus,
                button:focus {
                    outline: 2px solid var(--primary-green);
                    outline-offset: 2px;
                }

                /* Responsive */
                @media (min-width: 769px) and (max-width: 1024px) {
                    .hero-content {
                        flex-direction: column;
                    }
                    
                    .hero-stats {
                        width: 100%;
                        max-width: 600px;
                        margin: 0 auto;
                    }

                    .stats-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }

                    .features-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .values-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .app-content-wrapper {
                        grid-template-columns: 1fr;
                    }

                    .app-visual-column {
                        order: -1;
                    }

                    .app-features-grid {
                        grid-template-columns: 1fr 1fr;
                    }

                    .app-stats-grid {
                        grid-template-columns: repeat(4, 1fr);
                    }
                }

                @media (max-width: 768px) {
                    .app-wrapper {
                        padding: 1.5rem 1rem;
                        border-radius: var(--radius-lg);
                    }

                    .app-features-grid {
                        grid-template-columns: 1fr;
                    }

                    .app-stats-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .app-download-buttons {
                        flex-direction: column;
                    }

                    .app-qr-container {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .app-trust-badges {
                        flex-wrap: wrap;
                    }

                    .phone-container {
                        width: 220px;
                        height: 400px;
                    }

                    .phone-mockup {
                        border-radius: 30px;
                        padding: 8px;
                    }

                    .phone-notch {
                        width: 80px;
                        height: 18px;
                        top: 8px;
                    }

                    .phone-notch::before {
                        width: 40px;
                        height: 4px;
                    }

                    .phone-screen {
                        border-radius: 22px;
                    }

                    .phone-status-bar {
                        padding: 8px 12px 2px;
                        font-size: 9px;
                    }

                    .phone-app-content {
                        padding: 4px 8px 8px;
                    }

                    .phone-app-logo {
                        font-size: 12px;
                    }

                    .phone-app-logo svg {
                        font-size: 14px;
                    }

                    .phone-app-badge {
                        font-size: 7px;
                        padding: 1px 6px;
                    }

                    .phone-dashboard {
                        padding: 6px 8px;
                    }

                    .dashboard-title {
                        font-size: 9px;
                    }

                    .dashboard-date {
                        font-size: 7px;
                    }

                    .phone-metrics {
                        gap: 4px;
                    }

                    .phone-metric {
                        padding: 4px 6px;
                    }

                    .metric-value {
                        font-size: 13px;
                    }

                    .metric-label {
                        font-size: 7px;
                    }

                    .metric-status {
                        font-size: 6px;
                    }

                    .rec-title {
                        font-size: 8px;
                    }

                    .rec-item {
                        font-size: 8px;
                        padding: 2px 0;
                    }

                    .rec-tag {
                        font-size: 7px;
                        padding: 1px 4px;
                    }

                    .phone-bottom-nav {
                        font-size: 13px;
                        padding: 4px 0 2px;
                    }

                    .phone-home-button {
                        width: 30px;
                        height: 3px;
                        bottom: 6px;
                    }

                    .phone-float-element {
                        font-size: 16px;
                    }

                    .float-1 {
                        top: -12px;
                        right: -12px;
                    }

                    .float-2 {
                        bottom: 30px;
                        left: -16px;
                    }

                    .float-3 {
                        bottom: -10px;
                        right: 10px;
                    }

                    .app-stat-card {
                        padding: 0.5rem;
                    }

                    .app-stat-number {
                        font-size: 0.9rem;
                    }

                    .app-stat-label {
                        font-size: 0.55rem;
                    }

                    .app-feature-item {
                        padding: 0.5rem 0.75rem;
                    }

                    .app-feature-icon-wrapper {
                        width: 28px;
                        height: 28px;
                        font-size: 14px;
                    }

                    .app-feature-content h4 {
                        font-size: 0.8rem;
                    }

                    .app-feature-content p {
                        font-size: 0.7rem;
                    }
                }

                @media (max-width: 480px) {
                    .hero-title {
                        font-size: 2rem;
                    }

                    .container {
                        padding: 0 16px;
                    }

                    .app-wrapper {
                        padding: 1rem 0.75rem;
                    }

                    .app-stats-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 0.5rem;
                    }

                    .impact-grid {
                        grid-template-columns: 1fr 1fr;
                    }

                    .waitlist-benefits {
                        grid-template-columns: 1fr;
                    }

                    .cta-actions {
                        flex-direction: column;
                    }

                    .cta-features {
                        grid-template-columns: 1fr;
                    }

                    .qr-code-wrapper {
                        width: 100%;
                    }

                    .phone-container {
                        width: 200px;
                        height: 380px;
                    }

                    .phone-mockup {
                        border-radius: 24px;
                        padding: 6px;
                    }

                    .phone-screen {
                        border-radius: 18px;
                    }

                    .phone-status-bar {
                        padding: 6px 10px 2px;
                        font-size: 8px;
                    }

                    .phone-app-content {
                        padding: 4px 6px 6px;
                    }

                    .phone-dashboard {
                        padding: 4px 6px;
                    }

                    .phone-metrics {
                        grid-template-columns: 1fr;
                        gap: 3px;
                    }

                    .phone-metric {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 3px 6px;
                    }

                    .metric-label {
                        font-size: 7px;
                    }

                    .metric-value {
                        font-size: 12px;
                    }

                    .metric-status {
                        font-size: 6px;
                    }

                    .phone-recommendations {
                        display: none;
                    }

                    .phone-bottom-nav {
                        font-size: 12px;
                        padding: 3px 0 2px;
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    * {
                        animation-duration: 0.01ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: 0.01ms !important;
                    }
                    
                    .scroll-line::after,
                    .qr-pulse,
                    .phone-float-element {
                        animation: none !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default Products;
